export type IndividualFieldConfig = { [key: string]: string };
export type CombinedFieldConfig = { all?: string[] };

export type ResourceConfig = CombinedFieldConfig & IndividualFieldConfig;

/**
 * @param {T} resourceConfig
 * @return {(T & { all: string[] })}
 * @template T,S
 */
function createResourceFieldConfig(
  resourceConfig: IndividualFieldConfig,
): ResourceConfig {
  const copied = { ...resourceConfig } as ResourceConfig;
  copied.all = ['foo', 'bar'];
  return copied;
}

export type ResourcesWithFieldSearch =
  | 'AllergyIntolerance'
  | 'Appointment'
  | 'BodySite'
  | 'CarePlan'
  | 'Condition'
  | 'Consent'
  | 'Device'
  | 'DiagnosticReport'
  | 'DocumentReference'
  | 'Encounter'
  | 'FamilyMemberHistory'
  | 'ImagingStudy'
  | 'Immunization'
  | 'Location'
  | 'Measure'
  | 'MeasureReport'
  | 'Media'
  | 'MedicationAdministration'
  | 'MedicationDispense'
  | 'MedicationRequest'
  | 'MedicationStatement'
  | 'Observation'
  | 'Goal'
  | 'Organization'
  | 'Patient'
  | 'Practitioner'
  | 'Procedure'
  | 'Provenance'
  | 'Questionnaire'
  | 'QuestionnaireResponse'
  | 'ReferralRequest'
  | 'Specimen'
  | 'Sequence'
  | 'Task';

export type ResourceFieldConfigHash = {
  [key in ResourcesWithFieldSearch]: ResourceConfig;
};

export const resourceFields: ResourceFieldConfigHash = {
  AllergyIntolerance: {},
  Appointment: {},
  BodySite: {},
  CarePlan: {},
  Condition: createResourceFieldConfig({
    bodySite: '/condition/bodySite/coding',
    category: '/condition/category/coding',
    clinicalStatus: '/condition/clinicalStatus',
    code: '/condition/code/coding',
    evidence: '/condition/evidence/code/coding',
    severity: '/condition/severity/coding',
    stage: '/condition/stage/summary/coding',
    verificationStatus: '/condition/verificationStatus',
  }),
  Consent: {},
  Device: {},
  DiagnosticReport: {},
  DocumentReference: {
    typeCoding: '/documentReference/type/coding',
  },
  Encounter: {
    class: '/encounter/class',
    priority: '/encounter/priority/coding',
    reason: '/encounter/reason/coding',
    type: '/encounter/type/coding',
  },
  FamilyMemberHistory: {},
  ImagingStudy: {},
  Immunization: {
    status: '/immunization/status',
    vaccineCode: '/immunization/vaccineCode/coding',
    site: '/immunization/site/coding',
    route: '/immunization/route/coding',
    reason: '/immunization/explanation/reason/coding',
    reasonNotGiven: '/immunization/explanation/reasonNotGiven/coding',
  },
  Location: {},
  Measure: createResourceFieldConfig({
    jurisdiction: '/measure/jurisdiction/coding',
    topic: '/measure/topic/coding',
  }),
  MeasureReport: {},
  Media: {},
  MedicationAdministration: createResourceFieldConfig({
    category: '/medicationAdministration/category/coding',
    medicationCodeableConcept:
      '/medicationAdministration/medicationCodeableConcept/coding',
    contained: '/medicationAdministration/contained/code/coding',
  }),
  MedicationDispense: createResourceFieldConfig({
    category: '/medicationDispense/category/coding',
    medicationCodeableConcept:
      '/medicationDispense/medicationCodeableConcept/coding',
    contained: '/medicationDispense/contained/code/coding',
  }),
  MedicationRequest: createResourceFieldConfig({
    category: '/medicationRequest/category/coding',
    medicationCodeableConcept:
      '/medicationRequest/medicationCodeableConcept/coding',
    contained: '/medicationRequest/contained/code/coding',
  }),
  MedicationStatement: createResourceFieldConfig({
    category: '/medicationStatement/category/coding',
    medicationCodeableConcept:
      '/medicationStatement/medicationCodeableConcept/coding',
    contained: '/medicationStatement/contained/code/coding',
  }),
  Observation: createResourceFieldConfig({
    category: '/observation/category/coding',
    code: '/observation/code/coding',
    componentCode: '/observation/component/code/coding',
    componentDataAbsentReason: '/observation/component/dataAbsentReason/coding',
    componentValueCodeableConcept:
      '/observation/component/valueCodeableConcept/coding',
    dataAbsentReason: '/observation/dataAbsentReason/coding',
    method: '/observation/method/coding',
    related: '/observation/related/type',
    status: '/observation/status',
    valueCodeableConcept: '/observation/valueCodeableConcept/coding',
  }),
  Goal: createResourceFieldConfig({
    description: '/goal/description/coding',
    category: '/goal/category/coding',
    target: '/goal/target/measure/coding',
  }),
  Organization: {},
  Patient: createResourceFieldConfig({
    address: '/patient/address/use',
    animalBreed: '/patient/animal/breed/coding',
    animalSpecies: '/patient/animal/species/coding',
    gender: '/patient/gender',
    language: '/patient/communication/language/coding',
    tag: '/patient/meta/tag',
  }),
  Practitioner: {},
  Procedure: createResourceFieldConfig({
    category: '/procedure/category/coding',
    code: '/procedure/code/coding',
  }),
  Provenance: {},
  Questionnaire: {},
  QuestionnaireResponse: {},
  ReferralRequest: {},
  Specimen: {
    type: '/specimen/type/coding',
  },
  Sequence: createResourceFieldConfig({
    chromosome: '/sequence/referenceSeq/chromosome/coding',
    type: '/sequence/type',
  }),
  Task: {},
};

export function getFieldsForResourceType(
  type: ResourcesWithFieldSearch,
): ResourceConfig {
  return resourceFields[type] || {};
}
